import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../types';

export interface TopChannel {
  channelId: string;
  title: string;
  videoCount: number;
  totalViews: number;
}

interface UseTopChannelsResult {
  channels: TopChannel[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Fetch the top-channels ranking that powers the /channels hub page. */
export function useTopChannels(enabled = true): UseTopChannelsResult {
  const [channels, setChannels] = useState<TopChannel[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchChannels = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/channels');
      if (!response.ok) {
        const errorData = (await response.json()) as ApiError;
        throw new Error(errorData.error || 'Failed to fetch channels');
      }
      const data = (await response.json()) as { channels: TopChannel[] };
      setChannels(data.channels);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setChannels([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  return { channels, isLoading, error, refetch: fetchChannels };
}
